// Central kitchen audio engine. NEW-order sound is a mandatory operational feature: there is no
// mute/ON-OFF preference anywhere and no persisted `soundEnabled` value is ever read. The only thing
// that decides whether sound actually plays is the REAL AudioContext state (browser/PWA policy).

let _ctx = null;

export function getCtx() {
  if (!_ctx) {
    try { _ctx = new (window.AudioContext || window.webkitAudioContext)(); } catch { _ctx = null; }
  }
  return _ctx;
}

/** Authoritative readiness — never trust a stored flag, always the live context state. */
export function audioReady() {
  return !!_ctx && _ctx.state === "running";
}

/**
 * Best-effort unlock/resume. Must be called inside a user gesture the first time on most devices.
 * Returns true only once the context is verified as `running`.
 */
export async function unlockAudio() {
  const ctx = getCtx();
  if (!ctx) return false;
  if (ctx.state !== "running") {
    // resume() can stay pending forever (iOS "interrupted", OS audio-focus loss) — never hang a caller.
    try {
      await Promise.race([ctx.resume(), new Promise((r) => setTimeout(r, 1200))]);
    } catch { /* device still requires a gesture */ }
  }
  return ctx.state === "running";
}

/** Loud, bright kitchen "ding-ding" order bell at full app volume — deliberately hard to miss. */
export function playNewOrderBell() {
  try {
    const ctx = getCtx();
    if (!ctx || ctx.state !== "running") return false;
    const master = ctx.createGain();
    master.gain.value = 1.0;
    master.connect(ctx.destination);
    const strike = (t0) => {
      [[1318.5, 1.0], [2637, 0.4], [3956, 0.2]].forEach(([freq, amp]) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "triangle"; o.frequency.value = freq;
        o.connect(g); g.connect(master);
        const s = ctx.currentTime + t0;
        g.gain.setValueAtTime(0.0001, s);
        g.gain.exponentialRampToValueAtTime(amp, s + 0.006);
        g.gain.exponentialRampToValueAtTime(0.0001, s + 0.45);
        o.start(s); o.stop(s + 0.5);
      });
    };
    strike(0); strike(0.18);
    return ctx.state === "running";
  } catch { return false; }
}

/** Distinct, discreet late-order alert (two soft descending tones) — different from the new-order chime. */
export function playLateBeep() {
  try {
    const ctx = getCtx();
    if (!ctx || ctx.state !== "running") return;
    const tone = (freq, start, dur) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "triangle"; o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
      g.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + start + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
      o.start(ctx.currentTime + start); o.stop(ctx.currentTime + start + dur + 0.02);
    };
    tone(660, 0, 0.28); tone(440, 0.22, 0.4);
  } catch { /* audio must never break the kitchen flow */ }
}

/**
 * Preparation reminder bells — deliberately different from the NEW ORDER chime, never continuous.
 * 10 min = one short bell, 5 min = two short, stronger bells.
 */
export function playReminderBell(strikes = 1) {
  try {
    const ctx = getCtx();
    if (!ctx || ctx.state !== "running") return;
    const master = ctx.createGain();
    master.gain.value = strikes > 1 ? 0.9 : 0.6;
    master.connect(ctx.destination);
    const strike = (t0) => {
      [[880, 1.0], [1760, 0.3]].forEach(([freq, amp]) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine"; o.frequency.value = freq;
        o.connect(g); g.connect(master);
        const s = ctx.currentTime + t0;
        g.gain.setValueAtTime(0.0001, s);
        g.gain.exponentialRampToValueAtTime(amp, s + 0.008);
        g.gain.exponentialRampToValueAtTime(0.0001, s + 0.35);
        o.start(s); o.stop(s + 0.4);
      });
    };
    for (let i = 0; i < strikes; i++) strike(i * 0.22);
  } catch { /* audio must never break the kitchen flow */ }
}

// ---- device-local multi-tab coordination (audio only; never restaurant-global) ----
// One tab per browser profile owns the audible NEW-order alarm for a given restaurant. The owner
// refreshes a heartbeat on every ring; a lock older than STALE_MS may be taken over by another tab.
const TAB_ID = Math.random().toString(36).slice(2);
const STALE_MS = 4000;
const lockKey = (rid) => `ponesi.newOrderAlarm.lock.${rid}`;

/** Claims/refreshes the audible slot for this tab. Returns true when this tab may ring. */
export function claimAlarmSlot(rid) {
  if (!audioReady()) return false;              // a muted/locked tab must never hold the slot
  const now = Date.now();
  try {
    const raw = JSON.parse(localStorage.getItem(lockKey(rid)) || "null");
    if (raw && raw.tabId !== TAB_ID && now - (raw.updatedAt || 0) < STALE_MS) return false;
    localStorage.setItem(lockKey(rid), JSON.stringify({ tabId: TAB_ID, restaurantId: rid, updatedAt: now }));
    // Re-read to confirm ownership (last writer wins → no doubled alarm on simultaneous takeover).
    const after = JSON.parse(localStorage.getItem(lockKey(rid)) || "null");
    return !!after && after.tabId === TAB_ID;
  } catch { return true; }                      // storage blocked → stay audible
}

export function releaseAlarmSlot(rid) {
  try {
    const raw = JSON.parse(localStorage.getItem(lockKey(rid)) || "null");
    if (raw && raw.tabId === TAB_ID) localStorage.removeItem(lockKey(rid));
  } catch { /* ignore */ }
}
